/**
 * Documents Domain Seed Data
 *
 * Contains all seed data for Document Pipeline Service (5):
 * - Documents (uploaded files)
 * - Document Chunks (processed text segments)
 * - Processing Jobs (async processing tasks)
 * - Search Indices (full-text search entries)
 * - Document Metadata (tags, categories)
 * - Audit Logs (document operations)
 * - Document Provenance (processing history)
 *
 * @see Issue #84 - Document Processing Pipeline
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// DOCUMENT IDs
// =============================================================================

export const DOCUMENT_IDS = {
  // OpsMatrix Capital documents
  FUNDING_LOAN_APP_PDF: seedId('doc-funding-loan-app', 1),
  FUNDING_APPRAISAL_REPORT: seedId('doc-funding-appraisal', 1),
  FUNDING_TITLE_COMMITMENT: seedId('doc-funding-title', 1),
  FUNDING_BORROWER_ID: seedId('doc-funding-borrower-id', 1),
  FUNDING_BANK_STATEMENTS: seedId('doc-funding-bank-stmt', 1),

  // 1Sharpe Capital documents
  SHARPE_CONSTRUCTION_PLANS: seedId('doc-sharpe-plans', 1),
  SHARPE_INSPECTION_PHOTOS: seedId('doc-sharpe-photos', 1),
  SHARPE_DRAW_REQUEST: seedId('doc-sharpe-draw-req', 1),

  // Pacific Bridge Fund documents
  PACIFIC_INVESTMENT_MEMO: seedId('doc-pacific-memo', 1),
  PACIFIC_TERM_SHEET: seedId('doc-pacific-term', 1),
} as const;

export type DocumentId = (typeof DOCUMENT_IDS)[keyof typeof DOCUMENT_IDS];

// =============================================================================
// DOCUMENT CHUNK IDs
// =============================================================================

export const DOCUMENT_CHUNK_IDS = {
  // Loan Application chunks
  LOAN_APP_CHUNK_1: seedId('chunk-loan-app-1', 1),
  LOAN_APP_CHUNK_2: seedId('chunk-loan-app-2', 2),
  LOAN_APP_CHUNK_3: seedId('chunk-loan-app-3', 3),

  // Appraisal Report chunks
  APPRAISAL_CHUNK_1: seedId('chunk-appraisal-1', 1),
  APPRAISAL_CHUNK_2: seedId('chunk-appraisal-2', 2),

  // Construction Plans chunks
  PLANS_CHUNK_1: seedId('chunk-plans-1', 1),
  PLANS_CHUNK_2: seedId('chunk-plans-2', 2),
} as const;

export type DocumentChunkId = (typeof DOCUMENT_CHUNK_IDS)[keyof typeof DOCUMENT_CHUNK_IDS];

// =============================================================================
// PROCESSING JOB IDs
// =============================================================================

export const PROCESSING_JOB_IDS = {
  // OpsMatrix Capital jobs
  FUNDING_LOAN_APP_JOB: seedId('job-funding-loan-app', 1),
  FUNDING_APPRAISAL_JOB: seedId('job-funding-appraisal', 1),
  FUNDING_TITLE_JOB: seedId('job-funding-title', 1),

  // 1Sharpe Capital jobs
  SHARPE_PLANS_JOB: seedId('job-sharpe-plans', 1),
  SHARPE_PHOTOS_JOB: seedId('job-sharpe-photos', 1),
} as const;

export type ProcessingJobId = (typeof PROCESSING_JOB_IDS)[keyof typeof PROCESSING_JOB_IDS];

// =============================================================================
// SEARCH INDEX IDs
// =============================================================================

export const SEARCH_INDEX_IDS = {
  FUNDING_LOAN_APP_INDEX: seedId('search-funding-loan-app', 1),
  FUNDING_APPRAISAL_INDEX: seedId('search-funding-appraisal', 1),
  SHARPE_PLANS_INDEX: seedId('search-sharpe-plans', 1),
  PACIFIC_MEMO_INDEX: seedId('search-pacific-memo', 1),
} as const;

export type SearchIndexId = (typeof SEARCH_INDEX_IDS)[keyof typeof SEARCH_INDEX_IDS];

// =============================================================================
// DOCUMENT METADATA IDs
// =============================================================================

export const DOCUMENT_METADATA_IDS = {
  // Loan Application metadata
  LOAN_APP_CATEGORY: seedId('meta-loan-app-cat', 1),
  LOAN_APP_LOAN_TYPE: seedId('meta-loan-app-type', 1),
  LOAN_APP_BORROWER: seedId('meta-loan-app-borrower', 1),

  // Appraisal metadata
  APPRAISAL_PROPERTY: seedId('meta-appraisal-prop', 1),
  APPRAISAL_VALUE: seedId('meta-appraisal-value', 1),

  // Construction Plans metadata
  PLANS_PROJECT: seedId('meta-plans-project', 1),
  PLANS_PHASE: seedId('meta-plans-phase', 1),
} as const;

export type DocumentMetadataId = (typeof DOCUMENT_METADATA_IDS)[keyof typeof DOCUMENT_METADATA_IDS];

// =============================================================================
// AUDIT LOG IDs
// =============================================================================

export const AUDIT_LOG_IDS = {
  // Loan Application audit trail
  LOAN_APP_UPLOADED: seedId('audit-loan-app-upload', 1),
  LOAN_APP_PROCESSING_STARTED: seedId('audit-loan-app-process', 1),
  LOAN_APP_PROCESSING_COMPLETED: seedId('audit-loan-app-complete', 1),

  // Appraisal audit trail
  APPRAISAL_UPLOADED: seedId('audit-appraisal-upload', 1),
  APPRAISAL_DOWNLOADED: seedId('audit-appraisal-download', 1),

  // Construction Plans audit trail
  PLANS_UPLOADED: seedId('audit-plans-upload', 1),
} as const;

export type AuditLogId = (typeof AUDIT_LOG_IDS)[keyof typeof AUDIT_LOG_IDS];

// =============================================================================
// DOCUMENT PROVENANCE IDs
// =============================================================================

export const DOCUMENT_PROVENANCE_IDS = {
  LOAN_APP_PROVENANCE: seedId('provenance-loan-app', 1),
  APPRAISAL_PROVENANCE: seedId('provenance-appraisal', 1),
  PLANS_PROVENANCE: seedId('provenance-plans', 1),
} as const;

export type DocumentProvenanceId = (typeof DOCUMENT_PROVENANCE_IDS)[keyof typeof DOCUMENT_PROVENANCE_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface DocumentSeedData {
  id: string;
  businessId: string;
  fileName: string;
  originalFileName: string;
  mimeType: string;
  size: number;
  gcsPath: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  errorMessage?: string;
  uploadedAt: string;
  processedAt?: string;
  completedAt?: string;
  chunkCount: number;
  embeddingModel?: string;
  embeddingDimension?: number;
}

export interface DocumentChunkSeedData {
  id: string;
  businessId: string;
  documentId: string;
  chunkIndex: number;
  text: string;
  tokenCount: number;
  embedding: number[];
  startPage?: number;
  endPage?: number;
  startPosition?: number;
  endPosition?: number;
  hasTable: boolean;
  sectionTitle?: string;
}

export interface ProcessingJobSeedData {
  id: string;
  businessId: string;
  documentId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'retrying';
  progress: number;
  retryCount: number;
  maxRetries: number;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface SearchIndexSeedData {
  id: string;
  businessId: string;
  documentId: string;
  fullText: string;
  keywords: string[];
}

export interface DocumentMetadataSeedData {
  id: string;
  businessId: string;
  documentId: string;
  key: string;
  value: string;
}

export interface AuditLogSeedData {
  id: string;
  businessId: string;
  documentId: string;
  action: 'uploaded' | 'processing_started' | 'processing_completed' | 'processing_failed' | 'downloaded' | 'deleted';
  actor: string;
  details: string;
  ipAddress?: string;
  createdAt: string;
}

export interface DocumentProvenanceSeedData {
  id: string;
  businessId: string;
  documentId: string;
  workflowId: string;
  runId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  inputHash: string;
  outputHash?: string;
  steps: Array<{
    step: string;
    startedAt: string;
    completedAt?: string;
    durationMs?: number;
    inputHash: string;
    outputHash?: string;
    success: boolean;
    metrics?: Record<string, unknown>;
    error?: string;
  }>;
  totalDurationMs?: number;
  completedAt?: string;
  errorMessage?: string;
}

// =============================================================================
// DOCUMENT DATA
// =============================================================================

export const DOCUMENTS: Record<keyof typeof DOCUMENT_IDS, DocumentSeedData> = {
  FUNDING_LOAN_APP_PDF: {
    id: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    fileName: 'loan-application-thompson-2026.pdf',
    originalFileName: 'Marcus Thompson - Loan Application.pdf',
    mimeType: 'application/pdf',
    size: 2456789,
    gcsPath: 'gs://opsmatrix-docs/funding/loans/loan-application-thompson-2026.pdf',
    status: 'completed',
    uploadedAt: '2026-01-20T10:00:00Z',
    processedAt: '2026-01-20T10:05:00Z',
    completedAt: '2026-01-20T10:08:00Z',
    chunkCount: 12,
    embeddingModel: 'text-embedding-3-small',
    embeddingDimension: 1536,
  },
  FUNDING_APPRAISAL_REPORT: {
    id: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    fileName: 'appraisal-456-oak-street.pdf',
    originalFileName: 'Appraisal Report - 456 Oak Street.pdf',
    mimeType: 'application/pdf',
    size: 5678901,
    gcsPath: 'gs://opsmatrix-docs/funding/appraisals/appraisal-456-oak-street.pdf',
    status: 'completed',
    uploadedAt: '2026-01-19T14:30:00Z',
    processedAt: '2026-01-19T14:35:00Z',
    completedAt: '2026-01-19T14:42:00Z',
    chunkCount: 24,
    embeddingModel: 'text-embedding-3-small',
    embeddingDimension: 1536,
  },
  FUNDING_TITLE_COMMITMENT: {
    id: DOCUMENT_IDS.FUNDING_TITLE_COMMITMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    fileName: 'title-commitment-456-oak.pdf',
    originalFileName: 'Title Commitment.pdf',
    mimeType: 'application/pdf',
    size: 1234567,
    gcsPath: 'gs://opsmatrix-docs/funding/title/title-commitment-456-oak.pdf',
    status: 'processing',
    uploadedAt: '2026-01-21T09:00:00Z',
    processedAt: '2026-01-21T09:02:00Z',
    chunkCount: 0,
  },
  FUNDING_BORROWER_ID: {
    id: DOCUMENT_IDS.FUNDING_BORROWER_ID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    fileName: 'id-thompson-marcus.jpg',
    originalFileName: 'Drivers License.jpg',
    mimeType: 'image/jpeg',
    size: 456789,
    gcsPath: 'gs://opsmatrix-docs/funding/identity/id-thompson-marcus.jpg',
    status: 'completed',
    uploadedAt: '2026-01-20T10:15:00Z',
    processedAt: '2026-01-20T10:16:00Z',
    completedAt: '2026-01-20T10:17:00Z',
    chunkCount: 1,
    embeddingModel: 'text-embedding-3-small',
    embeddingDimension: 1536,
  },
  FUNDING_BANK_STATEMENTS: {
    id: DOCUMENT_IDS.FUNDING_BANK_STATEMENTS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    fileName: 'bank-statements-thompson-q4.pdf',
    originalFileName: 'Bank Statements Oct-Dec 2025.pdf',
    mimeType: 'application/pdf',
    size: 3456789,
    gcsPath: 'gs://opsmatrix-docs/funding/financials/bank-statements-thompson-q4.pdf',
    status: 'queued',
    uploadedAt: '2026-01-21T11:00:00Z',
    chunkCount: 0,
  },
  SHARPE_CONSTRUCTION_PLANS: {
    id: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    fileName: 'construction-plans-riverside.pdf',
    originalFileName: 'Riverside Development - Construction Plans.pdf',
    mimeType: 'application/pdf',
    size: 15678901,
    gcsPath: 'gs://opsmatrix-docs/sharpe/construction/construction-plans-riverside.pdf',
    status: 'completed',
    uploadedAt: '2026-01-18T08:00:00Z',
    processedAt: '2026-01-18T08:10:00Z',
    completedAt: '2026-01-18T08:25:00Z',
    chunkCount: 45,
    embeddingModel: 'text-embedding-3-small',
    embeddingDimension: 1536,
  },
  SHARPE_INSPECTION_PHOTOS: {
    id: DOCUMENT_IDS.SHARPE_INSPECTION_PHOTOS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    fileName: 'inspection-riverside-jan21.zip',
    originalFileName: 'Site Inspection Photos - Jan 21 2026.zip',
    mimeType: 'application/zip',
    size: 45678901,
    gcsPath: 'gs://opsmatrix-docs/sharpe/inspections/inspection-riverside-jan21.zip',
    status: 'processing',
    uploadedAt: '2026-01-21T14:00:00Z',
    processedAt: '2026-01-21T14:05:00Z',
    chunkCount: 0,
  },
  SHARPE_DRAW_REQUEST: {
    id: DOCUMENT_IDS.SHARPE_DRAW_REQUEST,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    fileName: 'draw-request-riverside-3.pdf',
    originalFileName: 'Draw Request #3 - Riverside Development.pdf',
    mimeType: 'application/pdf',
    size: 987654,
    gcsPath: 'gs://opsmatrix-docs/sharpe/draws/draw-request-riverside-3.pdf',
    status: 'completed',
    uploadedAt: '2026-01-19T10:00:00Z',
    processedAt: '2026-01-19T10:02:00Z',
    completedAt: '2026-01-19T10:05:00Z',
    chunkCount: 8,
    embeddingModel: 'text-embedding-3-small',
    embeddingDimension: 1536,
  },
  PACIFIC_INVESTMENT_MEMO: {
    id: DOCUMENT_IDS.PACIFIC_INVESTMENT_MEMO,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    fileName: 'investment-memo-thompson-portfolio.pdf',
    originalFileName: 'Investment Memo - Thompson Portfolio Expansion.pdf',
    mimeType: 'application/pdf',
    size: 2345678,
    gcsPath: 'gs://opsmatrix-docs/pacific/memos/investment-memo-thompson-portfolio.pdf',
    status: 'completed',
    uploadedAt: '2026-01-17T15:00:00Z',
    processedAt: '2026-01-17T15:05:00Z',
    completedAt: '2026-01-17T15:12:00Z',
    chunkCount: 18,
    embeddingModel: 'text-embedding-3-small',
    embeddingDimension: 1536,
  },
  PACIFIC_TERM_SHEET: {
    id: DOCUMENT_IDS.PACIFIC_TERM_SHEET,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    fileName: 'term-sheet-thompson-v2.pdf',
    originalFileName: 'Term Sheet - Thompson Portfolio v2.pdf',
    mimeType: 'application/pdf',
    size: 567890,
    gcsPath: 'gs://opsmatrix-docs/pacific/terms/term-sheet-thompson-v2.pdf',
    status: 'completed',
    uploadedAt: '2026-01-20T16:00:00Z',
    processedAt: '2026-01-20T16:02:00Z',
    completedAt: '2026-01-20T16:04:00Z',
    chunkCount: 5,
    embeddingModel: 'text-embedding-3-small',
    embeddingDimension: 1536,
  },
};

// =============================================================================
// DOCUMENT CHUNK DATA
// =============================================================================

// Sample embedding vector (simplified for seed data - actual would be 1536 dimensions)
const sampleEmbedding = Array.from({ length: 10 }, () => Math.random() * 2 - 1);

export const DOCUMENT_CHUNKS: Record<keyof typeof DOCUMENT_CHUNK_IDS, DocumentChunkSeedData> = {
  LOAN_APP_CHUNK_1: {
    id: DOCUMENT_CHUNK_IDS.LOAN_APP_CHUNK_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    chunkIndex: 0,
    text: 'LOAN APPLICATION\n\nBorrower Information\nName: Marcus Thompson\nEntity: Thompson Real Estate Holdings LLC\nSSN: XXX-XX-1234\nPhone: (512) 555-0123\nEmail: marcus.thompson@investorpro.com\n\nProperty Information\nAddress: 456 Oak Street, Austin, TX 78701\nProperty Type: Single Family Rental\nPurchase Price: $425,000\nRehab Budget: $75,000',
    tokenCount: 89,
    embedding: sampleEmbedding,
    startPage: 1,
    endPage: 1,
    startPosition: 0,
    endPosition: 450,
    hasTable: false,
    sectionTitle: 'Loan Application',
  },
  LOAN_APP_CHUNK_2: {
    id: DOCUMENT_CHUNK_IDS.LOAN_APP_CHUNK_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    chunkIndex: 1,
    text: 'Loan Terms Requested\n\nLoan Amount: $375,000\nLoan Type: Fix and Flip\nTerm: 12 months\nInterest Rate: Market rate\nExit Strategy: Refinance into long-term rental loan\n\nExperience\nPrevious Properties: 12\nYears in Real Estate: 5\nTotal Portfolio Value: $3.2M',
    tokenCount: 72,
    embedding: sampleEmbedding,
    startPage: 1,
    endPage: 2,
    startPosition: 451,
    endPosition: 780,
    hasTable: false,
    sectionTitle: 'Loan Terms Requested',
  },
  LOAN_APP_CHUNK_3: {
    id: DOCUMENT_CHUNK_IDS.LOAN_APP_CHUNK_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    chunkIndex: 2,
    text: 'Financial Summary\n\n| Asset Type | Value |\n|------------|-------|\n| Real Estate | $3,200,000 |\n| Cash/Liquid | $450,000 |\n| Retirement | $180,000 |\n| Other | $75,000 |\n| Total Assets | $3,905,000 |\n\nLiabilities: $1,850,000\nNet Worth: $2,055,000',
    tokenCount: 85,
    embedding: sampleEmbedding,
    startPage: 2,
    endPage: 2,
    startPosition: 781,
    endPosition: 1100,
    hasTable: true,
    sectionTitle: 'Financial Summary',
  },
  APPRAISAL_CHUNK_1: {
    id: DOCUMENT_CHUNK_IDS.APPRAISAL_CHUNK_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    chunkIndex: 0,
    text: 'APPRAISAL REPORT\n\nProperty Address: 456 Oak Street, Austin, TX 78701\nEffective Date: January 15, 2026\nAppraiser: John Reynolds, MAI\nLicense #: TX-12345\n\nAs-Is Value: $415,000\nAfter-Repair Value (ARV): $525,000\n\nProperty Description:\n3 bedroom, 2 bathroom single family home built in 1985. 1,850 square feet on 0.18 acre lot.',
    tokenCount: 95,
    embedding: sampleEmbedding,
    startPage: 1,
    endPage: 1,
    startPosition: 0,
    endPosition: 420,
    hasTable: false,
    sectionTitle: 'Appraisal Report',
  },
  APPRAISAL_CHUNK_2: {
    id: DOCUMENT_CHUNK_IDS.APPRAISAL_CHUNK_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    chunkIndex: 1,
    text: 'Comparable Sales Analysis\n\n| Address | Sale Price | Sq Ft | Price/Sq Ft |\n|---------|-----------|-------|-------------|\n| 123 Maple Ave | $510,000 | 1,920 | $266 |\n| 789 Cedar Ln | $495,000 | 1,780 | $278 |\n| 234 Elm St | $535,000 | 1,900 | $282 |\n\nAdjusted Average: $283/sq ft\nSubject Property ARV: $525,000 ($284/sq ft)',
    tokenCount: 110,
    embedding: sampleEmbedding,
    startPage: 3,
    endPage: 4,
    startPosition: 1500,
    endPosition: 2100,
    hasTable: true,
    sectionTitle: 'Comparable Sales Analysis',
  },
  PLANS_CHUNK_1: {
    id: DOCUMENT_CHUNK_IDS.PLANS_CHUNK_1,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    chunkIndex: 0,
    text: 'RIVERSIDE DEVELOPMENT\nConstruction Plans - Phase 1\n\nProject Overview:\n- 24-unit multifamily development\n- 3 buildings, 8 units each\n- Total square footage: 28,800 sq ft\n- Estimated completion: Q4 2026\n\nGeneral Contractor: BuildRight Construction LLC\nArchitect: Modern Design Associates\nPermit #: AUS-2026-0145',
    tokenCount: 78,
    embedding: sampleEmbedding,
    startPage: 1,
    endPage: 1,
    startPosition: 0,
    endPosition: 380,
    hasTable: false,
    sectionTitle: 'Project Overview',
  },
  PLANS_CHUNK_2: {
    id: DOCUMENT_CHUNK_IDS.PLANS_CHUNK_2,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    chunkIndex: 1,
    text: 'Construction Budget Summary\n\n| Phase | Description | Budget |\n|-------|-------------|--------|\n| 1 | Site Prep & Foundation | $850,000 |\n| 2 | Framing & Roofing | $1,200,000 |\n| 3 | MEP Rough-In | $680,000 |\n| 4 | Interior Finish | $920,000 |\n| 5 | Exterior & Landscaping | $350,000 |\n| Total | | $4,000,000 |',
    tokenCount: 95,
    embedding: sampleEmbedding,
    startPage: 2,
    endPage: 2,
    startPosition: 381,
    endPosition: 750,
    hasTable: true,
    sectionTitle: 'Construction Budget Summary',
  },
};

// =============================================================================
// PROCESSING JOB DATA
// =============================================================================

export const PROCESSING_JOBS: Record<keyof typeof PROCESSING_JOB_IDS, ProcessingJobSeedData> = {
  FUNDING_LOAN_APP_JOB: {
    id: PROCESSING_JOB_IDS.FUNDING_LOAN_APP_JOB,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    status: 'completed',
    progress: 100,
    retryCount: 0,
    maxRetries: 3,
    startedAt: '2026-01-20T10:05:00Z',
    completedAt: '2026-01-20T10:08:00Z',
    createdAt: '2026-01-20T10:00:00Z',
  },
  FUNDING_APPRAISAL_JOB: {
    id: PROCESSING_JOB_IDS.FUNDING_APPRAISAL_JOB,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    status: 'completed',
    progress: 100,
    retryCount: 0,
    maxRetries: 3,
    startedAt: '2026-01-19T14:35:00Z',
    completedAt: '2026-01-19T14:42:00Z',
    createdAt: '2026-01-19T14:30:00Z',
  },
  FUNDING_TITLE_JOB: {
    id: PROCESSING_JOB_IDS.FUNDING_TITLE_JOB,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_TITLE_COMMITMENT,
    status: 'processing',
    progress: 45,
    retryCount: 0,
    maxRetries: 3,
    startedAt: '2026-01-21T09:02:00Z',
    createdAt: '2026-01-21T09:00:00Z',
  },
  SHARPE_PLANS_JOB: {
    id: PROCESSING_JOB_IDS.SHARPE_PLANS_JOB,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    status: 'completed',
    progress: 100,
    retryCount: 0,
    maxRetries: 3,
    startedAt: '2026-01-18T08:10:00Z',
    completedAt: '2026-01-18T08:25:00Z',
    createdAt: '2026-01-18T08:00:00Z',
  },
  SHARPE_PHOTOS_JOB: {
    id: PROCESSING_JOB_IDS.SHARPE_PHOTOS_JOB,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_INSPECTION_PHOTOS,
    status: 'processing',
    progress: 32,
    retryCount: 0,
    maxRetries: 3,
    startedAt: '2026-01-21T14:05:00Z',
    createdAt: '2026-01-21T14:00:00Z',
  },
};

// =============================================================================
// SEARCH INDEX DATA
// =============================================================================

export const SEARCH_INDICES: Record<keyof typeof SEARCH_INDEX_IDS, SearchIndexSeedData> = {
  FUNDING_LOAN_APP_INDEX: {
    id: SEARCH_INDEX_IDS.FUNDING_LOAN_APP_INDEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    fullText:
      'LOAN APPLICATION Borrower Information Name Marcus Thompson Entity Thompson Real Estate Holdings LLC Property Address 456 Oak Street Austin TX 78701 Property Type Single Family Rental Purchase Price 425000 Rehab Budget 75000 Loan Amount 375000 Loan Type Fix and Flip Term 12 months Experience Previous Properties 12 Years in Real Estate 5 Total Portfolio Value 3.2M Financial Summary Total Assets 3905000 Liabilities 1850000 Net Worth 2055000',
    keywords: [
      'loan application',
      'marcus thompson',
      'austin tx',
      'fix and flip',
      'single family rental',
      'oak street',
      'thompson real estate',
      'portfolio',
      'rehab',
      'underwriting',
    ],
  },
  FUNDING_APPRAISAL_INDEX: {
    id: SEARCH_INDEX_IDS.FUNDING_APPRAISAL_INDEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    fullText:
      'APPRAISAL REPORT Property Address 456 Oak Street Austin TX 78701 Appraiser John Reynolds MAI As-Is Value 415000 After-Repair Value ARV 525000 Property Description 3 bedroom 2 bathroom single family home 1850 square feet Comparable Sales Analysis Market Analysis Neighborhood Analysis Condition Report',
    keywords: [
      'appraisal',
      'arv',
      'oak street',
      'austin tx',
      'comparable sales',
      'property value',
      'single family',
      'market analysis',
      'condition report',
      'mai',
    ],
  },
  SHARPE_PLANS_INDEX: {
    id: SEARCH_INDEX_IDS.SHARPE_PLANS_INDEX,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    fullText:
      'RIVERSIDE DEVELOPMENT Construction Plans Phase 1 Project Overview 24-unit multifamily development 3 buildings Total square footage 28800 Estimated completion Q4 2026 General Contractor BuildRight Construction Architect Modern Design Associates Construction Budget Site Prep Foundation Framing Roofing MEP Interior Finish Exterior Landscaping',
    keywords: [
      'construction plans',
      'riverside development',
      'multifamily',
      'buildright',
      'framing',
      'foundation',
      'mep',
      'landscaping',
      'permit',
      'budget',
    ],
  },
  PACIFIC_MEMO_INDEX: {
    id: SEARCH_INDEX_IDS.PACIFIC_MEMO_INDEX,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    documentId: DOCUMENT_IDS.PACIFIC_INVESTMENT_MEMO,
    fullText:
      'INVESTMENT MEMO Thompson Portfolio Expansion Investment Committee Review Loan Request 2500000 Collateral Properties 5 single family rentals Austin TX market Expected Return 12 percent IRR Risk Assessment Low to Moderate Recommendation Approve with standard terms',
    keywords: [
      'investment memo',
      'thompson portfolio',
      'investment committee',
      'austin tx',
      'irr',
      'risk assessment',
      'collateral',
      'loan request',
      'pacific bridge',
      'approval',
    ],
  },
};

// =============================================================================
// DOCUMENT METADATA DATA
// =============================================================================

export const DOCUMENT_METADATA: Record<keyof typeof DOCUMENT_METADATA_IDS, DocumentMetadataSeedData> = {
  LOAN_APP_CATEGORY: {
    id: DOCUMENT_METADATA_IDS.LOAN_APP_CATEGORY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    key: 'category',
    value: 'loan_application',
  },
  LOAN_APP_LOAN_TYPE: {
    id: DOCUMENT_METADATA_IDS.LOAN_APP_LOAN_TYPE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    key: 'loan_type',
    value: 'fix_and_flip',
  },
  LOAN_APP_BORROWER: {
    id: DOCUMENT_METADATA_IDS.LOAN_APP_BORROWER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    key: 'borrower_name',
    value: 'Marcus Thompson',
  },
  APPRAISAL_PROPERTY: {
    id: DOCUMENT_METADATA_IDS.APPRAISAL_PROPERTY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    key: 'property_address',
    value: '456 Oak Street, Austin, TX 78701',
  },
  APPRAISAL_VALUE: {
    id: DOCUMENT_METADATA_IDS.APPRAISAL_VALUE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    key: 'arv',
    value: '525000',
  },
  PLANS_PROJECT: {
    id: DOCUMENT_METADATA_IDS.PLANS_PROJECT,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    key: 'project_name',
    value: 'Riverside Development',
  },
  PLANS_PHASE: {
    id: DOCUMENT_METADATA_IDS.PLANS_PHASE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    key: 'phase',
    value: '1',
  },
};

// =============================================================================
// AUDIT LOG DATA
// =============================================================================

export const AUDIT_LOGS: Record<keyof typeof AUDIT_LOG_IDS, AuditLogSeedData> = {
  LOAN_APP_UPLOADED: {
    id: AUDIT_LOG_IDS.LOAN_APP_UPLOADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    action: 'uploaded',
    actor: USER_IDS.JAMES_WILSON,
    details: JSON.stringify({
      fileName: 'Marcus Thompson - Loan Application.pdf',
      size: 2456789,
      mimeType: 'application/pdf',
      source: 'web_upload',
    }),
    ipAddress: '192.168.1.100',
    createdAt: '2026-01-20T10:00:00Z',
  },
  LOAN_APP_PROCESSING_STARTED: {
    id: AUDIT_LOG_IDS.LOAN_APP_PROCESSING_STARTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    action: 'processing_started',
    actor: 'system',
    details: JSON.stringify({
      jobId: PROCESSING_JOB_IDS.FUNDING_LOAN_APP_JOB,
      embeddingModel: 'text-embedding-3-small',
    }),
    createdAt: '2026-01-20T10:05:00Z',
  },
  LOAN_APP_PROCESSING_COMPLETED: {
    id: AUDIT_LOG_IDS.LOAN_APP_PROCESSING_COMPLETED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    action: 'processing_completed',
    actor: 'system',
    details: JSON.stringify({
      jobId: PROCESSING_JOB_IDS.FUNDING_LOAN_APP_JOB,
      chunkCount: 12,
      durationMs: 180000,
    }),
    createdAt: '2026-01-20T10:08:00Z',
  },
  APPRAISAL_UPLOADED: {
    id: AUDIT_LOG_IDS.APPRAISAL_UPLOADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    action: 'uploaded',
    actor: USER_IDS.MARIA_RODRIGUEZ,
    details: JSON.stringify({
      fileName: 'Appraisal Report - 456 Oak Street.pdf',
      size: 5678901,
      mimeType: 'application/pdf',
      source: 'email_attachment',
    }),
    ipAddress: '192.168.1.105',
    createdAt: '2026-01-19T14:30:00Z',
  },
  APPRAISAL_DOWNLOADED: {
    id: AUDIT_LOG_IDS.APPRAISAL_DOWNLOADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    action: 'downloaded',
    actor: USER_IDS.JOHN_SMITH,
    details: JSON.stringify({
      reason: 'underwriting_review',
      downloadFormat: 'original',
    }),
    ipAddress: '192.168.1.110',
    createdAt: '2026-01-21T09:15:00Z',
  },
  PLANS_UPLOADED: {
    id: AUDIT_LOG_IDS.PLANS_UPLOADED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    action: 'uploaded',
    actor: USER_IDS.ALEX_KUMAR,
    details: JSON.stringify({
      fileName: 'Riverside Development - Construction Plans.pdf',
      size: 15678901,
      mimeType: 'application/pdf',
      source: 'api_upload',
    }),
    ipAddress: '10.0.0.50',
    createdAt: '2026-01-18T08:00:00Z',
  },
};

// =============================================================================
// DOCUMENT PROVENANCE DATA
// =============================================================================

export const DOCUMENT_PROVENANCES: Record<keyof typeof DOCUMENT_PROVENANCE_IDS, DocumentProvenanceSeedData> = {
  LOAN_APP_PROVENANCE: {
    id: DOCUMENT_PROVENANCE_IDS.LOAN_APP_PROVENANCE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_LOAN_APP_PDF,
    workflowId: 'doc-process-loan-app-001',
    runId: 'run-loan-app-001-abc123',
    status: 'completed',
    inputHash: 'sha256:a1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456',
    outputHash: 'sha256:f6e5d4c3b2a1098765432109876543210fedcba0987654321fedcba0987654',
    steps: [
      {
        step: 'download',
        startedAt: '2026-01-20T10:05:00Z',
        completedAt: '2026-01-20T10:05:02Z',
        durationMs: 2000,
        inputHash: 'sha256:gcs-path-hash',
        outputHash: 'sha256:a1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456',
        success: true,
        metrics: { bytesDownloaded: 2456789 },
      },
      {
        step: 'docling_convert',
        startedAt: '2026-01-20T10:05:02Z',
        completedAt: '2026-01-20T10:06:30Z',
        durationMs: 88000,
        inputHash: 'sha256:a1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456',
        outputHash: 'sha256:converted-markdown-hash',
        success: true,
        metrics: { pageCount: 8, tableCount: 2 },
      },
      {
        step: 'chunking',
        startedAt: '2026-01-20T10:06:30Z',
        completedAt: '2026-01-20T10:06:45Z',
        durationMs: 15000,
        inputHash: 'sha256:converted-markdown-hash',
        outputHash: 'sha256:chunks-hash',
        success: true,
        metrics: { chunkCount: 12, tokenCount: 2450 },
      },
      {
        step: 'embed_vectors',
        startedAt: '2026-01-20T10:06:45Z',
        completedAt: '2026-01-20T10:07:30Z',
        durationMs: 45000,
        inputHash: 'sha256:chunks-hash',
        outputHash: 'sha256:embeddings-hash',
        success: true,
        metrics: { embeddingDimension: 1536, model: 'text-embedding-3-small' },
      },
      {
        step: 'index_typesense',
        startedAt: '2026-01-20T10:07:30Z',
        completedAt: '2026-01-20T10:07:45Z',
        durationMs: 15000,
        inputHash: 'sha256:chunks-hash',
        outputHash: 'sha256:typesense-index-hash',
        success: true,
        metrics: { indexedCount: 12 },
      },
      {
        step: 'index_qdrant',
        startedAt: '2026-01-20T10:07:45Z',
        completedAt: '2026-01-20T10:08:00Z',
        durationMs: 15000,
        inputHash: 'sha256:embeddings-hash',
        outputHash: 'sha256:f6e5d4c3b2a1098765432109876543210fedcba0987654321fedcba0987654',
        success: true,
        metrics: { indexedCount: 12 },
      },
    ],
    totalDurationMs: 180000,
    completedAt: '2026-01-20T10:08:00Z',
  },
  APPRAISAL_PROVENANCE: {
    id: DOCUMENT_PROVENANCE_IDS.APPRAISAL_PROVENANCE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    documentId: DOCUMENT_IDS.FUNDING_APPRAISAL_REPORT,
    workflowId: 'doc-process-appraisal-001',
    runId: 'run-appraisal-001-def456',
    status: 'completed',
    inputHash: 'sha256:appraisal-input-hash-123456789',
    outputHash: 'sha256:appraisal-output-hash-987654321',
    steps: [
      {
        step: 'download',
        startedAt: '2026-01-19T14:35:00Z',
        completedAt: '2026-01-19T14:35:05Z',
        durationMs: 5000,
        inputHash: 'sha256:gcs-appraisal-path',
        outputHash: 'sha256:appraisal-input-hash-123456789',
        success: true,
        metrics: { bytesDownloaded: 5678901 },
      },
      {
        step: 'docling_convert',
        startedAt: '2026-01-19T14:35:05Z',
        completedAt: '2026-01-19T14:38:00Z',
        durationMs: 175000,
        inputHash: 'sha256:appraisal-input-hash-123456789',
        outputHash: 'sha256:appraisal-markdown-hash',
        success: true,
        metrics: { pageCount: 24, tableCount: 8 },
      },
      {
        step: 'chunking',
        startedAt: '2026-01-19T14:38:00Z',
        completedAt: '2026-01-19T14:38:30Z',
        durationMs: 30000,
        inputHash: 'sha256:appraisal-markdown-hash',
        outputHash: 'sha256:appraisal-chunks-hash',
        success: true,
        metrics: { chunkCount: 24, tokenCount: 5200 },
      },
      {
        step: 'embed_vectors',
        startedAt: '2026-01-19T14:38:30Z',
        completedAt: '2026-01-19T14:40:30Z',
        durationMs: 120000,
        inputHash: 'sha256:appraisal-chunks-hash',
        outputHash: 'sha256:appraisal-embeddings-hash',
        success: true,
        metrics: { embeddingDimension: 1536, model: 'text-embedding-3-small' },
      },
      {
        step: 'index_typesense',
        startedAt: '2026-01-19T14:40:30Z',
        completedAt: '2026-01-19T14:41:00Z',
        durationMs: 30000,
        inputHash: 'sha256:appraisal-chunks-hash',
        outputHash: 'sha256:appraisal-typesense-hash',
        success: true,
        metrics: { indexedCount: 24 },
      },
      {
        step: 'index_qdrant',
        startedAt: '2026-01-19T14:41:00Z',
        completedAt: '2026-01-19T14:42:00Z',
        durationMs: 60000,
        inputHash: 'sha256:appraisal-embeddings-hash',
        outputHash: 'sha256:appraisal-output-hash-987654321',
        success: true,
        metrics: { indexedCount: 24 },
      },
    ],
    totalDurationMs: 420000,
    completedAt: '2026-01-19T14:42:00Z',
  },
  PLANS_PROVENANCE: {
    id: DOCUMENT_PROVENANCE_IDS.PLANS_PROVENANCE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    documentId: DOCUMENT_IDS.SHARPE_CONSTRUCTION_PLANS,
    workflowId: 'doc-process-plans-001',
    runId: 'run-plans-001-ghi789',
    status: 'completed',
    inputHash: 'sha256:plans-input-hash-456789123',
    outputHash: 'sha256:plans-output-hash-321987654',
    steps: [
      {
        step: 'download',
        startedAt: '2026-01-18T08:10:00Z',
        completedAt: '2026-01-18T08:10:15Z',
        durationMs: 15000,
        inputHash: 'sha256:gcs-plans-path',
        outputHash: 'sha256:plans-input-hash-456789123',
        success: true,
        metrics: { bytesDownloaded: 15678901 },
      },
      {
        step: 'docling_convert',
        startedAt: '2026-01-18T08:10:15Z',
        completedAt: '2026-01-18T08:18:00Z',
        durationMs: 465000,
        inputHash: 'sha256:plans-input-hash-456789123',
        outputHash: 'sha256:plans-markdown-hash',
        success: true,
        metrics: { pageCount: 45, tableCount: 12 },
      },
      {
        step: 'chunking',
        startedAt: '2026-01-18T08:18:00Z',
        completedAt: '2026-01-18T08:19:00Z',
        durationMs: 60000,
        inputHash: 'sha256:plans-markdown-hash',
        outputHash: 'sha256:plans-chunks-hash',
        success: true,
        metrics: { chunkCount: 45, tokenCount: 9800 },
      },
      {
        step: 'embed_vectors',
        startedAt: '2026-01-18T08:19:00Z',
        completedAt: '2026-01-18T08:22:00Z',
        durationMs: 180000,
        inputHash: 'sha256:plans-chunks-hash',
        outputHash: 'sha256:plans-embeddings-hash',
        success: true,
        metrics: { embeddingDimension: 1536, model: 'text-embedding-3-small' },
      },
      {
        step: 'index_typesense',
        startedAt: '2026-01-18T08:22:00Z',
        completedAt: '2026-01-18T08:23:00Z',
        durationMs: 60000,
        inputHash: 'sha256:plans-chunks-hash',
        outputHash: 'sha256:plans-typesense-hash',
        success: true,
        metrics: { indexedCount: 45 },
      },
      {
        step: 'index_qdrant',
        startedAt: '2026-01-18T08:23:00Z',
        completedAt: '2026-01-18T08:25:00Z',
        durationMs: 120000,
        inputHash: 'sha256:plans-embeddings-hash',
        outputHash: 'sha256:plans-output-hash-321987654',
        success: true,
        metrics: { indexedCount: 45 },
      },
    ],
    totalDurationMs: 900000,
    completedAt: '2026-01-18T08:25:00Z',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllDocuments(workspace?: string): DocumentSeedData[] {
  const documents = Object.values(DOCUMENTS);
  if (!workspace) return documents;
  return documents.map((d) => ({ ...d, fileName: `${workspace}-${d.fileName}` }));
}

export function getDocumentsByBusiness(businessId: string): DocumentSeedData[] {
  return Object.values(DOCUMENTS).filter((d) => d.businessId === businessId);
}

export function getDocumentsByStatus(status: DocumentSeedData['status']): DocumentSeedData[] {
  return Object.values(DOCUMENTS).filter((d) => d.status === status);
}

export function getCompletedDocuments(): DocumentSeedData[] {
  return getDocumentsByStatus('completed');
}

export function getProcessingDocuments(): DocumentSeedData[] {
  return getDocumentsByStatus('processing');
}

export function getAllDocumentChunks(): DocumentChunkSeedData[] {
  return Object.values(DOCUMENT_CHUNKS);
}

export function getChunksByDocument(documentId: string): DocumentChunkSeedData[] {
  return Object.values(DOCUMENT_CHUNKS).filter((c) => c.documentId === documentId);
}

export function getChunksWithTables(): DocumentChunkSeedData[] {
  return Object.values(DOCUMENT_CHUNKS).filter((c) => c.hasTable);
}

export function getAllProcessingJobs(): ProcessingJobSeedData[] {
  return Object.values(PROCESSING_JOBS);
}

export function getProcessingJobsByStatus(status: ProcessingJobSeedData['status']): ProcessingJobSeedData[] {
  return Object.values(PROCESSING_JOBS).filter((j) => j.status === status);
}

export function getProcessingJobsByBusiness(businessId: string): ProcessingJobSeedData[] {
  return Object.values(PROCESSING_JOBS).filter((j) => j.businessId === businessId);
}

export function getAllSearchIndices(): SearchIndexSeedData[] {
  return Object.values(SEARCH_INDICES);
}

export function getSearchIndicesByBusiness(businessId: string): SearchIndexSeedData[] {
  return Object.values(SEARCH_INDICES).filter((s) => s.businessId === businessId);
}

export function getAllDocumentMetadata(): DocumentMetadataSeedData[] {
  return Object.values(DOCUMENT_METADATA);
}

export function getMetadataByDocument(documentId: string): DocumentMetadataSeedData[] {
  return Object.values(DOCUMENT_METADATA).filter((m) => m.documentId === documentId);
}

export function getAllAuditLogs(): AuditLogSeedData[] {
  return Object.values(AUDIT_LOGS);
}

export function getAuditLogsByDocument(documentId: string): AuditLogSeedData[] {
  return Object.values(AUDIT_LOGS).filter((a) => a.documentId === documentId);
}

export function getAuditLogsByAction(action: AuditLogSeedData['action']): AuditLogSeedData[] {
  return Object.values(AUDIT_LOGS).filter((a) => a.action === action);
}

export function getAuditLogsByActor(actor: string): AuditLogSeedData[] {
  return Object.values(AUDIT_LOGS).filter((a) => a.actor === actor);
}

export function getAllDocumentProvenances(): DocumentProvenanceSeedData[] {
  return Object.values(DOCUMENT_PROVENANCES);
}

export function getProvenanceByDocument(documentId: string): DocumentProvenanceSeedData | undefined {
  return Object.values(DOCUMENT_PROVENANCES).find((p) => p.documentId === documentId);
}

export function getProvenancesByStatus(status: DocumentProvenanceSeedData['status']): DocumentProvenanceSeedData[] {
  return Object.values(DOCUMENT_PROVENANCES).filter((p) => p.status === status);
}
